<template>
  <div class="admin-container">
    <LoaderOverlay v-if="loading" />
    <!-- Unauthorized State -->
    <EmptyState
      v-if="!loading && !hasAdminRole"
      :isUnauthorized="true"
    />
    <template v-else>
      <!-- Tabs Navigation -->
      <nav class="tabs-nav">
        <a
          v-for="tab in tabs"
          :key="tab.id"
          @click.prevent="activeTab = tab.id"
          :class="{ 'is-active': activeTab === tab.id }"
          class="tabs-nav__link"
          href="#"
        >
          {{ tab.label }}
        </a>
      </nav>
      <!-- Admin Content -->
      <div class="admin-content">
        <AdminDashboard
          v-if="activeTab === 'dashboard'"
          @loading="loading = $event"
        />
        <TripsManager
          v-if="activeTab === 'trips'"
          @loading="loading = $event"
        />
        <TicketsManager
          v-if="activeTab === 'tickets'"
          @loading="loading = $event"
        />
        <VehiclesManager
          v-if="activeTab === 'vehicles'"
          @loading="loading = $event"
        />
        <CompaniesManager
          v-if="activeTab === 'companies'"
          @loading="loading = $event"
        />
        <AddressesManager
          v-if="activeTab === 'addresses'"
          @loading="loading = $event"
        />
        <UsersManager
          v-if="activeTab === 'users'"
          @loading="loading = $event"
        />
        <RatingsManager
          v-if="activeTab === 'ratings'"
          @loading="loading = $event"
        />
        <SettingsManager
            v-if="activeTab === 'settings'"
            @loading="loading = $event"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { config } from '@/config/env.js'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import EmptyState from '@/components/EmptyState.vue'
import AdminDashboard from '@/components/Admin/AdminDashboard.vue'
import TripsManager from '@/components/Admin/TripsManager.vue'
import TicketsManager from '@/components/Admin/TicketsManager.vue'
import VehiclesManager from '@/components/Admin/VehiclesManager.vue'
import CompaniesManager from '@/components/Admin/CompaniesManager.vue'
import AddressesManager from '@/components/Admin/AddressesManager.vue'
import UsersManager from '@/components/Admin/UsersManager.vue'
import RatingsManager from '@/components/Admin/RatingsManager.vue'
import SettingsManager from '@/components/Admin/SettingsManager.vue'

const router = useRouter()
const userStore = useUserStore()
const activeTab = ref('dashboard')
const loading = ref(false)

const hasAdminRole = computed(() => {
  return userStore.user?.roles?.includes('ROLE_ADMIN') || false
})

const tabs = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'trips', label: 'Trajets' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'addresses', label: 'Adresses' },
  { id: 'vehicles', label: 'Véhicules' },
  { id: 'companies', label: 'Compagnies' },
  { id: 'ratings', label: 'Avis' },
  { id: 'settings', label: 'Paramètres' }
]

onMounted(() => {
  if (!hasAdminRole.value) {
    loading.value = false
  }
})
</script>

<style scoped>
.admin-container {
  background-color: var(--color-grey-light);
  padding: 1rem;
  width: 100%;
}
.admin-content {
  background: var(--bg-primary);
  border-radius: 0 0 8px 8px;
  padding: 2rem;
  box-shadow: var(--admin-shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
:deep(.date) {
  margin: 0;
  color: var(--admin-muted);
  font-size: 0.9rem;
  text-transform: capitalize;
}
:deep(.filters) {
  gap: 0;
}
:deep(.filters .btn-group:first-child) {
  border-bottom: none;
  align-items: center;
}
:deep(.filters.form-group) {
  gap: 1rem;
}
:deep(.filters.form-group > div:last-child) {
  align-items: center;
}
:deep(.filters .btn-group) {
  justify-content: space-between;
  align-items: flex-end;
}
:deep(.filters-group) {
  display: flex;
  flex-direction: column;
  margin-right: 1rem;
  flex: 1;
}
:deep(.page-header),.page-header {
  padding: 0 0 2rem 0;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
}

:deep(.table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: var(--fs-s);
}

:deep(.table th) {
  padding: 1.25rem 0.75rem;
  text-align: left;
  border-bottom: 2px solid var(--color-grey-medium);
  background: var(--color-grey-light);
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

:deep(.table td) {
  padding: 0.75rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  color: var(--text-color-primary);
}

:deep(.table td:last-child) {
}

:deep(.row) { transition: background-color 0.2s; }
/* :deep(.row:nth-child(even)) { background: var(--color-grey-light); } */
:deep(.row:hover) { background: var(--bg-black-alpha); }

.th--sortable { cursor: pointer; user-select: none; }
.th--sortable:hover { background: var(--admin-bg-soft); }

:deep(.sort-icon) {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: var(--fs-s);
  color: var(--admin-muted);
}

:deep(.actions) {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  flex: 1;
}

:deep(.thumbnail-upload),
:deep(.logo-upload) {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

:deep(.thumbnail-preview),
:deep(.logo-preview) {
  position: relative;
  width: 100%;
  max-width: 200px;
  aspect-ratio: 16/9;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 2px solid var(--color-gray-300, #d1d5db);
}

:deep(.thumbnail-preview img),
:deep(.logo-preview img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:deep(.thumbnail-preview .btn),
:deep(.logo-preview .btn) {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.25rem 0.5rem;
}

</style>
